import ArrowRightAltRoundedIcon from "@mui/icons-material/ArrowRightAltRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import type { PortalRole } from "../../../../api/types/accessTypes";
import EmailSuccessDialogIcon from "@entrilia/shared/icons/EmailSuccessDialogIcon";
import PortalRoleChip from "../../../common/PortalRoleChip";
import type { ContactPermissionsChange } from "./matrixHelper";
import { resolvePortalRoles } from "./matrixHelper";

const RoleChange = ({ original, modified }: { original?: PortalRole[]; modified: PortalRole[] }) => {
  return (
    <Stack direction={"row"} spacing={0.6} sx={{ alignItems: "center" }}>
      <PortalRoleChip role={resolvePortalRoles(original)} />
      <ArrowRightAltRoundedIcon color="secondary" />
      <PortalRoleChip role={resolvePortalRoles(modified)} />
    </Stack>
  );
};

const SingleContactChange = ({ contact, original, modified }: ContactPermissionsChange) => {
  const theme = useTheme();
  return (
    <Stack spacing={2} sx={{ alignItems: "center" }}>
      <RoleChange original={original?.roles} modified={modified.roles} />
      <Typography color="textSecondary" sx={{ textAlign: "center" }}>
        Upon making this change,
        <b style={{ color: theme.palette.text.primary }}>{` ${contact.name} <${contact.email}>`}</b> may receive an
        invitation email either immediately (if related investors have already published documents on the portal) or at
        a later time when such documents are published.
      </Typography>
    </Stack>
  );
};

const MultipleContactChanges = ({ changes }: { changes: ContactPermissionsChange[] }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean>(false);
  return (
    <Box>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        Upon making this change, <b style={{ color: theme.palette.text.primary }}>{changes.length} contacts</b> may
        receive an invitation email either immediately (if related investors have already published documents on the
        portal) or at a later time when such documents are published.
      </Typography>
      <Accordion
        onChange={(_, newExpanded) => setExpanded(newExpanded)}
        expanded={expanded}
        disableGutters
        elevation={0}
        sx={{ width: "100%", border: 1, borderColor: "divider" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreRoundedIcon />}
          sx={{
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": { transform: "rotate(180deg)" },
            borderBottom: expanded ? 1 : 0,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2">Details</Typography>
        </AccordionSummary>
        <AccordionDetails sx={(t) => ({ maxHeight: t.spacing(20), p: 0, overflow: "auto" })}>
          {changes.map((change) => (
            <Grid
              container
              spacing={2}
              key={change.contact.id}
              sx={{
                alignItems: "center",
                p: 2,
                width: "100%",
                "&:not(:last-child)": { borderBottom: 1, borderColor: "divider" },
              }}
            >
              <Grid size={4}>
                <TypographyTooltipEllipsis text={change.contact.name} />
              </Grid>
              <Grid size={4}>
                <Typography color="textSecondary">{change.contact.email}</Typography>
              </Grid>
            </Grid>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

interface Props {
  open: boolean;
  changes: ContactPermissionsChange[];
  onSubmit: () => void;
  onClose: () => void;
}

const SendInvitationsConfirmationDialog = ({ open, onSubmit, onClose, changes }: Props) => {
  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Invitation Email</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <EmailSuccessDialogIcon sx={{ height: 80, width: 80 }} />
          <Typography variant="h6">You are about to set the Investor Portal Role:</Typography>
          {changes.length === 1 && <SingleContactChange {...defined(changes[0])} />}
          {changes.length > 1 && <MultipleContactChanges changes={changes} />}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button onClick={onSubmit} variant="contained">
          Confirm
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default SendInvitationsConfirmationDialog;
